import { FC } from 'react';
import { Box, Typography } from '@mui/material';

import styles from './DelayChart.module.scss';
import { FlightDelayPrediction } from '../../../../../../../types/activity.types';

interface DelayChartProps {
  predictions: FlightDelayPrediction[];
}

interface ChartData {
  label: string;
  shortLabel: string;
  probability: number;
  height: number;
}

const DelayChart: FC<DelayChartProps> = ({ predictions }) => {
  const chartData: ChartData[] = predictions.map(prediction => {
    const percentage = Math.round(prediction.probability * 100 * 100) / 100;

    let shortLabel = prediction.label;
    switch (prediction.label) {
      case '30m':
        shortLabel = '30m';
        break;
      case '1h':
        shortLabel = '1h';
        break;
      case '2h':
        shortLabel = '2h';
        break;
      case '3h-cancelled':
        shortLabel = '3h-Cancel';
        break;
      default:
        shortLabel = prediction.label;
    }

    return {
      label: prediction.label,
      shortLabel,
      probability: percentage,
      height: percentage,
    };
  });

  const maxValue = Math.max(...chartData.map(d => d.probability));
  const scaledData = chartData.map(data => ({
    ...data,
    height: maxValue > 0 ? (data.probability / maxValue) * 100 : 0,
  }));

  const yAxisLabels = ['100%', '75%', '50%', '25%', '0%'];

  return (
    <Box className={styles.chart}>
      <Box className={styles.yAxis}>
        {yAxisLabels.map((label, index) => (
          <Typography
            key={label}
            variant="h12Semibold"
            className={styles.yAxisLabel}
          >
            {label}
          </Typography>
        ))}
      </Box>

      <Box className={styles.chartArea}>
        <Box className={styles.gridLines}>
          {yAxisLabels.slice(0, -1).map((_, index) => (
            <Box
              key={index}
              className={styles.gridLine}
            />
          ))}
        </Box>

        <Box className={styles.zeroLabel}>
          <Typography
            variant="h12Semibold"
            className={styles.xAxisLabel}
          >
            0
          </Typography>
        </Box>

        <Box className={styles.barsContainer}>
          {scaledData.map((data, index) => (
            <Box
              key={`${data.label}-${index}`}
              className={styles.barWrapper}
            >
              <Box className={styles.barContainer}>
                <Box
                  className={styles.bar}
                  style={{ height: `${data.height}%` }}
                ></Box>
              </Box>
            </Box>
          ))}
        </Box>

        <Box className={styles.labelsContainer}>
          {scaledData.map((data, index) => (
            <Box
              key={`label-${data.label}-${index}`}
              className={styles.labelWrapper}
            >
              <Typography
                variant="h12Semibold"
                className={styles.xAxisLabel}
              >
                {data.shortLabel}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default DelayChart;
